import { ILoginData, loadByAltId, logout } from "common/api/user.service";
import type { ThunkResult } from "common/reducers";
import type { RootDispatchType, RootGetState } from "common/store";
import { authService, authErrorMessage, auth0RefreshTokenPlaceholder } from "common/services/Auth.service";
import { UserActions } from "common/reducers/user.actions";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { AuthActions } from "common/reducers/auth.actions";
import { DANGER, addFlashAlert } from "common/components/FlashAlert";
import { AppMessageActions } from "common/reducers/appMessage.actions";
import { model as appMessagesModel } from "common/reducers/appMessage.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { currentUser } from "common/selectors/user.selectors";
import { resetSurfacedMessages } from "common/helpers/SurfacedMessages";
import { trackEventHelper } from "common/helpers/Analytics";
import { trackNrEvent, noticeError } from "common/helpers/NewRelic";
import { accessById } from "common/selectors/access.selectors";
import { accessUid } from "common/selectors/auth.selectors";
import { Auth0Client, User as OidcUser } from "@auth0/auth0-spa-js";
import { JobActions } from "common/reducers/job.actions";

export interface ILoginCallbackResult {
  success: boolean;
  appState?: {
    redirectTo?: string;
  };
}

export const AuthAsyncActions = {
  logout: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      const authType = authService.tokenType(authService.getRefreshToken());
      const refreshToken = authService.getRefreshToken();
      authService.cancelTokenRefresh();
      authService.clearRefreshToken();
      authService.clearRsfAuth();
      authService.clearUser();
      dispatch(JobActions.receiveJobParams({}));

      trackEventHelper({
        category: "User",
        action: "logout",
        authType,
      });

      switch (authType) {
        case "occ":
          const userManager = await authService.userManager;
          await userManager.logout();
          break;
        case "dabella":
          await authService.dabellaUserManager.signOut();
          authService.dabellaUserManager.stop();
          authService.dabellaUserManager.tokenManager.clear();
          break;
        default:
      }

      await logout(refreshToken);
      clearAuth(dispatch);
    };
  },
  changeAccessUid: (uid: string | undefined, accessId?: number) => {
    return (dispatch: RootDispatchType, getState: RootGetState) => {
      if (accessId) {
        uid = accessById(getState(), { accessId })?.uid;
      }
      if (!uid) {
        addFlashAlert(DANGER, "Access UID not found.");
        return;
      }
      // Redux holds the uid only after an in-app switch, so on a cold load the session is its only source.
      const previous = accessUid(getState()) || authService.getAccessUid();
      const changed = Boolean(previous) && previous !== uid;

      dispatch(JobActions.receiveJobParams({}));
      if (changed) {
        clearAccessSpecificData(dispatch);
      }
      authService.setAccessUid(uid);
      dispatch(AuthActions.setAccessUid(uid));
    };
  },
  refreshToken: (token: string): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const tokenType = authService.tokenType(token);

      switch (tokenType) {
        case "dabella": {
          try {
            const newToken = await authService.dabellaUserManager.getOrRenewAccessToken();

            if (newToken === null) {
              throw new Error("Failed to refresh Okta token");
            }
            dispatch(AuthActions.setTokens({ token: newToken, refreshToken: newToken }));

            const user = currentUser(getState());
            if (user && user.id && user.email) {
              authService.setCurrentUser(user.id, user.email, newToken);
              authService.setRefreshToken(newToken);
            }
            authService.dabellaUserManager.stop();
            authService.dabellaUserManager.start();
            trackEventHelper({
              action: "success refresh",
              category: "User",
              authType: "dabella",
            });
          } catch (e) {
            trackEventHelper({
              action: "failed refresh",
              category: "User",
              authType: "dabella",
            });
            throw e;
          }
          break;
        }
        case "occ":
        default: {
          const userManager = await authService.userManager;
          let newToken: string;
          let attempt = 1;

          try {
            newToken = await userManager.getTokenSilently();
          } catch (e) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            attempt = 2;
            try {
              newToken = await userManager.getTokenSilently();
            } catch (retryError) {
              noticeError(retryError, { source: "reactive", attempt: 2 });
              trackNrEvent("AuthTokenRefreshFailed", {
                source: "reactive",
                attempt: 2,
                errorMessage: retryError instanceof Error ? retryError.message : String(retryError),
                errorClass: retryError instanceof Error ? retryError.constructor.name : undefined,
              });
              trackEventHelper({ action: "failed refresh", category: "User", authType: "occ" });
              throw retryError;
            }
          }

          authService.applyOccToken(newToken!);
          if (attempt === 2) {
            trackNrEvent("AuthTokenRefreshSuccess", { source: "reactive", attempt: 2 });
          }
          trackEventHelper({ action: "success refresh", category: "User", authType: "occ" });
          break;
        }
      }
    };
  },
  auth0LoginCallback: (): ThunkResult<Promise<ILoginCallbackResult>> => {
    return async (dispatch: RootDispatchType) => {
      const userManager: Auth0Client = await authService.userManager;

      dispatch(IdsManagementActions.fetchModel("auth0Login"));

      const callbackResult: ILoginCallbackResult = { success: false };

      try {
        const redirectResult = await userManager.handleRedirectCallback();
        const redirectTo = redirectResult?.appState?.redirectTo;

        if (typeof redirectTo === "string") {
          callbackResult.appState = { redirectTo };
        }
      } catch (e) {
        console.error(e);
        userManager.logout();
        return callbackResult;
      }

      const user: OidcUser | undefined = await userManager.getUser();
      try {
        if (!user) {
          throw new Error("User data is undefined after login.");
        }

        const token = await userManager.getTokenSilently();

        const id = user.sub;
        if (!id) {
          throw new Error("User idp_id is undefined after login.");
        }
        const rsfUser = await loadByAltId("idp_id", id, token);

        const tokenData: ILoginData = {
          user: rsfUser,
          token: token,
          refresh_token: auth0RefreshTokenPlaceholder,
        };
        dispatchAuthUpdate(dispatch, tokenData);
        authService.setIdentityProvider("occ");
        authService.setupOccManualTokenRefresh();
        callbackResult.success = true;
        return callbackResult;
      } catch (e) {
        console.error("Auth Error: ", e);
        const error = authErrorMessage(e);
        addFlashAlert(DANGER, authErrorMessage(e), true);
        dispatch(
          RsfFormActions.updateError({
            rootPath: ["auth0Login"],
            name: "login",
            error,
          }),
        );
        dispatch(IdsManagementActions.setModelLoaded("auth0Login"));
        return callbackResult;
      }
    };
  },
  dabellaLoginCallback: (): ThunkResult<Promise<ILoginCallbackResult>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.fetchModel("dabellaLogin"));

      const callbackResult: ILoginCallbackResult = { success: false };

      try {
        const tokenData = await authService.handleDabellaRedirect();
        if (!tokenData) {
          throw new Error("Token data is undefined.");
        }
        dispatchAuthUpdate(dispatch, tokenData);
        dispatch(IdsManagementActions.setModelLoaded("dabellaLogin"));

        trackEventHelper({
          category: "User",
          action: "login",
          authType: "dabella",
        });
        if (tokenData.redirectTo) {
          callbackResult.appState = { redirectTo: tokenData.redirectTo };
        }
        callbackResult.success = true;
        return callbackResult;
      } catch (e) {
        trackEventHelper({
          category: "User",
          action: "failed-login",
          authType: "dabella",
        });
        console.error("Auth Error: ", e);
        addFlashAlert(DANGER, "Failed to log in.", true);

        dispatch(IdsManagementActions.setModelLoaded("dabellaLogin"));
        dispatch(RsfFormActions.updateError({ rootPath: ["dabellaLogin"], name: "login", error: "Failed to log in." }));

        return callbackResult;
      }
    };
  },
};

const clearAccessSpecificData = (dispatch: RootDispatchType) => {
  dispatch(IdsManagementActions.clearPage(appMessagesModel));
  dispatch(AppMessageActions.clearAppMessages());
  resetSurfacedMessages();
};

export const clearAuth = (dispatch: RootDispatchType) => {
  dispatch(AuthActions.setTokens({ token: null, refreshToken: null }));
  dispatch(AuthActions.setAccessUid(""));
  clearAccessSpecificData(dispatch);
  dispatch(UserActions.removeCurrentUser());
  dispatch(AuthActions.setAuthenticated(false));
};

export const dispatchAuthUpdate = (dispatch: RootDispatchType, tokenData: ILoginData): void => {
  authService.setRefreshToken(tokenData.refresh_token);
  authService.setAccessUid(tokenData.accessUid);
  authService.setCurrentUser(tokenData.user.id, tokenData.user.email, tokenData.token);

  dispatch(AuthActions.setAuthenticated(true));
  dispatch(AuthActions.setTokens({ token: tokenData.token, refreshToken: tokenData.refresh_token }));
  dispatch(UserActions.setCurrentUser(tokenData.user));
};
